import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type AdminCompany } from '../lib/api'
import { useCurrentUser, isSuperAdminRole } from '../lib/auth'

export const Route = createFileRoute('/admin/companies')({
  component: AdminCompaniesPage,
})

function AdminCompaniesPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => api.admin.companies.list(),
    enabled: isSuper,
  })

  const [renameTarget, setRenameTarget] = useState<AdminCompany | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'companies'] })
    qc.invalidateQueries({ queryKey: ['companies'] })
  }

  if (meLoading) return <PageShell><p className="text-[var(--muted)]">Hleð…</p></PageShell>

  if (!isSuper) {
    return (
      <PageShell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">Aðeins superadmin má stýra fyrirtækjaskránni.</p>
        </div>
      </PageShell>
    )
  }

  const companies = data?.companies ?? []

  return (
    <PageShell>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
        <h1 className="text-2xl font-bold text-[var(--fg)]">Fyrirtæki</h1>
        <p className="text-sm text-[var(--muted)] mt-1">
          Fyrirtækjaskrá kerfisins. „Habilis“ fyrirtæki verða til sjálfkrafa við innskráningu; þau má endurnefna eða eyða. Grunnfyrirtæki ÍGF eru varin.
        </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <SyncSvr2Customers />
          <AccessAuditButton />
        </div>
      </div>

      {isLoading && <p className="text-[var(--muted)]">Sæki fyrirtæki…</p>}
      {error && <p className="text-red-500">{(error as Error).message}</p>}

      {data && (
        <div className="bg-[var(--surface)] shadow-[var(--elev-sm)] rounded-xl overflow-hidden border border-[var(--border)]">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--surface-2)]">
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--muted)]">
                  <th className="px-4 py-3 font-medium">Nafn</th>
                  <th className="px-4 py-3 font-medium">Kennitala</th>
                  <th className="px-4 py-3 font-medium">Uppruni</th>
                  <th className="px-4 py-3 font-medium">Notendur</th>
                  <th className="px-4 py-3 font-medium text-right">Aðgerðir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {companies.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-[var(--faint)]">Engin fyrirtæki fundust.</td>
                  </tr>
                )}
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-[var(--surface-2)]">
                    <td className="px-4 py-3 text-[var(--fg)]">
                      {c.name}
                      <div className="text-[11px] text-[var(--faint)]">{c.id}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--muted)]">{c.kennitala || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          c.source === 'habilis'
                            ? 'bg-blue-50 text-blue-700 ring-blue-200'
                            : 'bg-gray-100 text-gray-600 ring-gray-200'
                        }`}
                      >
                        {c.source === 'habilis' ? 'Habilis' : 'Grunnur'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--muted)]">{c.userCount}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setRenameTarget(c)}
                          className="px-3 py-1.5 text-xs rounded-lg text-[var(--fg-soft)] hover:bg-[var(--surface-2)] border border-[var(--border)]"
                        >
                          Endurnefna
                        </button>
                        {c.source === 'habilis' && (
                          <DeleteButton company={c} onDone={invalidate} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {renameTarget && (
        <RenameModal
          company={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={() => { setRenameTarget(null); invalidate() }}
        />
      )}
    </PageShell>
  )
}

function DeleteButton({ company, onDone }: { company: AdminCompany; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const mutation = useMutation({
    mutationFn: () => api.admin.companies.remove(company.id),
    onSuccess: onDone,
  })

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="px-3 py-1.5 text-xs rounded-lg text-red-600 hover:bg-red-50 border border-red-200"
      >
        Eyða
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="px-3 py-1.5 text-xs rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
      >
        {mutation.isPending ? 'Eyði…' : 'Staðfesta'}
      </button>
      <button
        onClick={() => setConfirming(false)}
        className="px-3 py-1.5 text-xs rounded-lg text-[var(--muted)] hover:bg-[var(--surface-2)]"
      >
        Hætta við
      </button>
    </span>
  )
}

function RenameModal({ company, onClose, onDone }: { company: AdminCompany; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(company.name)
  const [shortName, setShortName] = useState(company.shortName)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.admin.companies.rename(company.id, { name: name.trim(), shortName: shortName.trim() || undefined }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (name.trim().length < 1) { setError('Nafn vantar'); return }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
          <h2 className="font-semibold text-[var(--fg)]">Endurnefna fyrirtæki</h2>
          <button onClick={onClose} className="text-[var(--faint)] hover:text-[var(--fg)] text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fg-soft)]">Nafn</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls()} />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--fg-soft)]">Stutt nafn</label>
            <input value={shortName} onChange={(e) => setShortName(e.target.value)} className={inputCls()} />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] rounded-lg">Hætta við</button>
            <button type="submit" disabled={mutation.isPending} className="px-4 py-2 text-sm text-[var(--on-brand)] bg-[var(--brand)] hover:bg-[var(--brand-strong)] disabled:opacity-50 rounded-lg">
              {mutation.isPending ? 'Vista…' : 'Vista'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}

function inputCls() {
  return 'mt-1 w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)] placeholder:text-[var(--faint)]'
}

function SyncSvr2Customers() {
  const mut = useMutation({ mutationFn: () => api.admin.svr2Customers.sync() })
  return (
    <div className="text-right">
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className="rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        {mut.isPending ? 'Samstilli…' : 'Samstilla viðskiptaskrá úr SVR'}
      </button>
      {mut.data && (
        <p className="mt-1.5 text-xs text-[var(--muted)]">
          {mut.data.total.toLocaleString('is-IS')} kennitölur ({mut.data.companies.toLocaleString('is-IS')} fyrirtæki) · {mut.data.added} nýjar · {mut.data.updated} uppfærðar
        </p>
      )}
      {mut.isError && <p className="mt-1.5 text-xs text-red-600">{(mut.error as Error).message}</p>}
    </div>
  )
}

function AccessAuditButton() {
  const mut = useMutation({ mutationFn: () => api.admin.accessAudit() })
  const a = mut.data
  const problemCount = a
    ? a.problems.orphanGrantsUserMissing.length + a.problems.grantsToUnknownCompany.length
    : 0
  return (
    <div className="text-right">
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-semibold text-[var(--fg)] hover:border-[var(--brand)] disabled:opacity-50"
      >
        {mut.isPending ? 'Skoða…' : 'Öryggisúttekt á aðgangi'}
      </button>
      {a && (
        <div className="mt-2 max-w-sm rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3 text-left text-xs">
          <p className="text-[var(--muted)]">
            {a.summary.regularUsers} viðskiptavina-notendur · {a.summary.totalGrants} aðgangar · mest {a.summary.maxCompaniesPerUser} fyrirtæki á notanda
          </p>
          {problemCount === 0 ? (
            <p className="mt-1 font-semibold text-[var(--acc-green)]">✓ Engin frávik — hver notandi hefur aðeins gildan, þekktan aðgang.</p>
          ) : (
            <div className="mt-1 space-y-1 text-red-600">
              {a.problems.orphanGrantsUserMissing.length > 0 && (
                <p>{a.problems.orphanGrantsUserMissing.length} aðgangar á notendur sem eru ekki til</p>
              )}
              {a.problems.grantsToUnknownCompany.length > 0 && (
                <p>{a.problems.grantsToUnknownCompany.length} aðgangar á óþekkt fyrirtæki</p>
              )}
            </div>
          )}
          {a.problems.privilegedUsersWithExplicitGrants.length > 0 && (
            <p className="mt-1 text-[var(--muted)]">
              Ath.: {a.problems.privilegedUsersWithExplicitGrants.length} admin/superadmin hafa líka skráða aðganga (skaðlaust — þeir hafa '*' hvort eð er).
            </p>
          )}
          {a.regularUsers.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[var(--brand)]">Sjá aðganga per notanda</summary>
              <ul className="mt-1 space-y-0.5 text-[var(--muted)]">
                {a.regularUsers.map((u) => (
                  <li key={u.email}>
                    {u.email}{u.active ? '' : ' (óvirkur)'}: {u.companies.join(', ') || '—'}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {mut.isError && <p className="mt-1.5 text-xs text-red-600">{(mut.error as Error).message}</p>}
    </div>
  )
}
